import React, { createContext, useContext, useState, useEffect } from 'react';
import { en } from './en';
import { de } from './de';
import { fr } from './fr';
import { it } from './it';
import { es } from './es';
import { countries_en, type Country } from './countries_en';
import { countries_de } from './countries_de';
import { countries_fr } from './countries_fr';
import { countries_it } from './countries_it';
import { countries_es } from './countries_es';

interface LanguageContextType {
  language: 'en' | 'de' | 'fr' | 'it' | 'es';
  setLanguage: (lang: 'en' | 'de' | 'fr' | 'it' | 'es') => void;
  t: typeof en & ((key: string) => string);
  countries: Country[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en,
  de,
  fr,
  it,
  es
};

const countriesMap = {
  en: countries_en,
  de: countries_de,
  fr: countries_fr,
  it: countries_it,
  es: countries_es
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<'en' | 'de' | 'fr' | 'it' | 'es'>(() => {
    const saved = localStorage.getItem('language');
    if (saved === 'en' || saved === 'de' || saved === 'fr' || saved === 'it' || saved === 'es') {
      return saved;
    }

    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('de')) return 'de';
    if (browserLang.startsWith('fr')) return 'fr';
    if (browserLang.startsWith('it')) return 'it';
    if (browserLang.startsWith('es')) return 'es';
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Create a hybrid t that works both as function and object
  const translationObj = translations[language as keyof typeof translations];

  const translateFunc = (key: string, params?: Record<string, any>): string => {
    // Handle nested keys like 'hero.headline1'
    const keys = key.split('.');
    let value: any = translationObj;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key; // Return the key if path doesn't exist
      }
    }

    let result = typeof value === 'string' ? value : key;

    // Replace placeholders like {{days}} with actual values
    if (params) {
      Object.keys(params).forEach(paramKey => {
        result = result.replace(`{{${paramKey}}}`, String(params[paramKey]));
      });
    }

    return result;
  };

  // Merge the function with the translation object properties
  const t = Object.assign(translateFunc, translationObj);

  // Get countries list for current language
  const countries = countriesMap[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, countries }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};